import { Head, router } from "@inertiajs/react"
import { Target, Plus, Search } from "lucide-react"
import { useState, useMemo } from "react"
import { MemberLayout } from "../components/MemberLayout"
import { formatDateRange } from "@/lib/format-date"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableHeader } from "@/components/ui/sortable-header"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { timeAgo } from "@/lib/time-ago"
import { ImperativeDialog } from "@/components/group-features/ImperativeDialog"
import {
  ImperativeDetailDialog,
  ImperativeStatusBadge,
} from "@/components/group-features/ImperativeDetailDialog"

const IMP_CATEGORY_ORDER = { professional: 0, personal: 1, spiritual: 2 }
const IMP_URGENCY_ORDER = { low: 0, medium: 1, high: 2 }

function impCompareWithNullsLast(a, b, direction) {
  const aMissing = a === null || a === undefined
  const bMissing = b === null || b === undefined
  if (aMissing && bMissing) return 0
  if (aMissing) return 1
  if (bMissing) return -1
  if (a < b) return direction === "asc" ? -1 : 1
  if (a > b) return direction === "asc" ? 1 : -1
  return 0
}

function imperativeStatusSortLabel(imp) {
  if (imp.status === "met") return "met"
  if (imp.status === "missed") return "missed"
  if (imp.overdue) return "overdue"
  return "active"
}

export default function ImperativesIndex({
  currentUser,
  imperatives = [],
  isCoach = false,
  groupMembers = [],
}) {
  // Dialog states
  const [createOpen, setCreateOpen] = useState(false)
  const [createKey, setCreateKey] = useState(0)

  const [editOpen, setEditOpen] = useState(false)
  const [editKey, setEditKey] = useState(0)
  const [editingImperative, setEditingImperative] = useState(null)

  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedImperative, setSelectedImperative] = useState(null)

  const [metTarget, setMetTarget] = useState(null)
  const [missedTarget, setMissedTarget] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const handleCreate = () => {
    setCreateKey((k) => k + 1)
    setCreateOpen(true)
  }

  const handleCardClick = (imperative) => {
    setSelectedImperative(imperative)
    setDetailOpen(true)
  }

  const handleEdit = (imperative) => {
    setEditingImperative(imperative)
    setEditKey((k) => k + 1)
    setEditOpen(true)
  }

  const handleMarkMet = () => {
    if (metTarget) {
      router.post(`/app/member/imperatives/${metTarget.id}/complete`, {}, {
        preserveScroll: true,
        onSuccess: () => setMetTarget(null),
      })
    }
  }

  const handleMarkMissed = () => {
    if (missedTarget) {
      router.post(`/app/member/imperatives/${missedTarget.id}/miss`, {}, {
        preserveScroll: true,
        onSuccess: () => setMissedTarget(null),
      })
    }
  }

  const handleDelete = () => {
    if (deleteTarget) {
      router.delete(`/app/member/imperatives/${deleteTarget.id}`, {
        preserveScroll: true,
        onSuccess: () => setDeleteTarget(null),
      })
    }
  }

  // Search and filter
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [categoryFilter, setCategoryFilter] = useState("all")
  const [urgencyFilter, setUrgencyFilter] = useState("all")
  const [dateFilter, setDateFilter] = useState("all")
  const [memberFilter, setMemberFilter] = useState("all")
  // Default: newest submitted first (task #517). Matches the other three
  // group-feature lists; the server already emits created_at DESC.
  const [sortKey, setSortKey] = useState("createdAt")
  const [direction, setDirection] = useState("desc")

  const handleSort = (column) => {
    if (sortKey !== column) {
      setSortKey(column)
      setDirection("asc")
      return
    }
    if (direction === "asc") {
      setDirection("desc")
      return
    }
    // desc → clear to default
    setSortKey("createdAt")
    setDirection("desc")
  }

  const memberOptions = useMemo(() => {
    const byId = new Map()
    imperatives.forEach((i) => {
      if (i.author?.id != null && !byId.has(i.author.id)) {
        byId.set(i.author.id, i.author)
      }
    })
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [imperatives])

  const filteredImperatives = useMemo(() => {
    let results = imperatives
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const isOverdue = (i) => i.status === "pending" && i.endDate && new Date(i.endDate) < today

    if (statusFilter === "pending") results = results.filter((i) => i.status === "pending" && !isOverdue(i))
    else if (statusFilter === "overdue") results = results.filter((i) => isOverdue(i))
    else if (statusFilter === "met") results = results.filter((i) => i.status === "met")
    else if (statusFilter === "missed") results = results.filter((i) => i.status === "missed")

    if (categoryFilter !== "all") results = results.filter((i) => i.category === categoryFilter)
    if (urgencyFilter !== "all") results = results.filter((i) => i.urgency === urgencyFilter)
    if (memberFilter !== "all") results = results.filter((i) => String(i.author?.id) === memberFilter)

    if (dateFilter === "overdue") {
      results = results.filter((i) => isOverdue(i))
    } else if (dateFilter === "due_this_week") {
      const sevenDays = new Date(today)
      sevenDays.setDate(sevenDays.getDate() + 7)
      results = results.filter((i) => {
        if (!i.endDate) return false
        const end = new Date(i.endDate)
        return end >= today && end <= sevenDays
      })
    } else if (dateFilter === "due_this_month") {
      const thirtyDays = new Date(today)
      thirtyDays.setDate(thirtyDays.getDate() + 30)
      results = results.filter((i) => {
        if (!i.endDate) return false
        const end = new Date(i.endDate)
        return end >= today && end <= thirtyDays
      })
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      results = results.filter(
        (i) => i.goal.toLowerCase().includes(q) || i.consequence?.toLowerCase().includes(q)
      )
    }
    const sorted = [...results].sort((a, b) => {
      let aVal
      let bVal
      if (sortKey === "endDate") {
        aVal = a.endDate ? new Date(a.endDate).getTime() : null
        bVal = b.endDate ? new Date(b.endDate).getTime() : null
      } else if (sortKey === "status") {
        aVal = imperativeStatusSortLabel(a)
        bVal = imperativeStatusSortLabel(b)
      } else if (sortKey === "goal") {
        aVal = a.goal?.toLowerCase() || null
        bVal = b.goal?.toLowerCase() || null
      } else if (sortKey === "author") {
        aVal = a.author?.name?.toLowerCase() || null
        bVal = b.author?.name?.toLowerCase() || null
      } else if (sortKey === "consequence") {
        aVal = a.consequence?.trim() ? a.consequence.toLowerCase() : null
        bVal = b.consequence?.trim() ? b.consequence.toLowerCase() : null
      } else if (sortKey === "createdAt") {
        aVal = a.createdAt ? new Date(a.createdAt).getTime() : null
        bVal = b.createdAt ? new Date(b.createdAt).getTime() : null
      } else if (sortKey === "category") {
        aVal = a.category != null ? IMP_CATEGORY_ORDER[a.category] : null
        bVal = b.category != null ? IMP_CATEGORY_ORDER[b.category] : null
      } else if (sortKey === "urgency") {
        aVal = a.urgency != null ? IMP_URGENCY_ORDER[a.urgency] : null
        bVal = b.urgency != null ? IMP_URGENCY_ORDER[b.urgency] : null
      }
      return impCompareWithNullsLast(aVal, bVal, direction)
    })
    return sorted
  }, [imperatives, statusFilter, categoryFilter, urgencyFilter, dateFilter, memberFilter, searchQuery, sortKey, direction])

  const isEmpty = imperatives.length === 0

  return (
    <MemberLayout currentUser={currentUser}>
      <Head title="Imperatives - Kingdom Factor" />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Imperatives</h1>
            <p className="text-zinc-600 mt-1">
              Set goals with deadlines and consequences.
            </p>
          </div>
          <Button variant="gradient" onClick={handleCreate} className="self-start sm:self-auto">
            <Plus className="h-4 w-4 mr-2" />
            New Imperative
          </Button>
        </div>

        {/* Empty state */}
        {isEmpty && (
          <div className="text-center py-12 text-zinc-500">
            <Target className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
            <p className="font-medium">No imperatives yet.</p>
            <p className="text-sm mt-1">Set a goal to get started.</p>
          </div>
        )}

        {!isEmpty && (
          <>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <Input
                  placeholder="Search imperatives..."
                  className="pl-9 bg-white border-zinc-300"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-36 bg-white border-zinc-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="met">Met</SelectItem>
                  <SelectItem value="missed">Missed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-40 bg-white border-zinc-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="spiritual">Spiritual</SelectItem>
                </SelectContent>
              </Select>
              <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
                <SelectTrigger className="w-full sm:w-36 bg-white border-zinc-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Urgency</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-full sm:w-40 bg-white border-zinc-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Dates</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="due_this_week">Due this week</SelectItem>
                  <SelectItem value="due_this_month">Due this month</SelectItem>
                </SelectContent>
              </Select>
              <Select value={memberFilter} onValueChange={setMemberFilter}>
                <SelectTrigger className="w-full sm:w-44 bg-white border-zinc-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Members</SelectItem>
                  {memberOptions.map((m) => (
                    <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            {filteredImperatives.length === 0 ? (
              <p className="text-center py-8 text-zinc-500 text-sm">No matching imperatives.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader column="goal" sortKey={sortKey} direction={direction} onSort={handleSort}>
                      Goal
                    </SortableHeader>
                    <SortableHeader column="endDate" sortKey={sortKey} direction={direction} onSort={handleSort} className="hidden sm:table-cell w-44">
                      Dates
                    </SortableHeader>
                    <SortableHeader column="consequence" sortKey={sortKey} direction={direction} onSort={handleSort} className="hidden md:table-cell">
                      Consequence
                    </SortableHeader>
                    <SortableHeader column="status" sortKey={sortKey} direction={direction} onSort={handleSort} className="w-28">
                      Status
                    </SortableHeader>
                    <SortableHeader column="author" sortKey={sortKey} direction={direction} onSort={handleSort} className="hidden sm:table-cell">
                      Author
                    </SortableHeader>
                    <SortableHeader column="createdAt" sortKey={sortKey} direction={direction} onSort={handleSort} className="w-20 text-right">
                      Date
                    </SortableHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredImperatives.map((imp) => (
                    <TableRow
                      key={imp.id}
                      className="cursor-pointer"
                      onClick={() => handleCardClick(imp)}
                    >
                      <TableCell className="whitespace-normal">
                        <span className="font-medium text-zinc-900 line-clamp-2">{imp.goal}</span>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-zinc-600">
                        {formatDateRange(imp.startDate, imp.endDate)}
                      </TableCell>
                      <TableCell className="hidden md:table-cell whitespace-normal text-sm text-zinc-500">
                        <span className="line-clamp-1">{imp.consequence}</span>
                      </TableCell>
                      <TableCell>
                        <ImperativeStatusBadge status={imp.status} overdue={imp.overdue} />
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-sm text-zinc-600">
                        {imp.author.name}
                      </TableCell>
                      <TableCell className="text-right text-sm text-zinc-500">
                        {timeAgo(imp.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </div>

      {/* Create dialog */}
      <ImperativeDialog
        key={`create-${createKey}`}
        open={createOpen}
        onOpenChange={setCreateOpen}
        groupMembers={groupMembers}
        isCoach={isCoach}
      />

      {/* Edit dialog */}
      <ImperativeDialog
        key={`edit-${editKey}`}
        open={editOpen}
        onOpenChange={setEditOpen}
        imperative={editingImperative}
        groupMembers={groupMembers}
        isCoach={isCoach}
      />

      {/* Detail dialog */}
      <ImperativeDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        imperative={selectedImperative}
        currentUserId={currentUser.id}
        isCoach={isCoach}
        onEdit={handleEdit}
        onMarkMet={setMetTarget}
        onMarkMissed={setMissedTarget}
        onDelete={setDeleteTarget}
      />

      {/* Mark Met confirmation */}
      <AlertDialog
        open={!!metTarget}
        onOpenChange={(open) => !open && setMetTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as Met</AlertDialogTitle>
            <AlertDialogDescription>
              Mark this imperative as met? The resolution date will be recorded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-green-600 text-white hover:bg-green-700"
              onClick={handleMarkMet}
            >
              Mark Met
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark Missed confirmation */}
      <AlertDialog
        open={!!missedTarget}
        onOpenChange={(open) => !open && setMissedTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as Missed</AlertDialogTitle>
            <AlertDialogDescription>
              Mark this imperative as missed? This signals the goal wasn&apos;t met by the due date
              {missedTarget?.consequence ? " — the consequence still applies." : "."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={handleMarkMissed}
            >
              Mark Missed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Imperative</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this imperative? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={handleDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MemberLayout>
  )
}
